import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Inventory as BacklogIcon } from "@mui/icons-material";

import { usePageTitle } from "../context/PageTitleContext";
import {
  useDistributionBacklog,
  usePendingDenials,
} from "../hooks/useDistributionCoverage";

/**
 * B3.33 — BacklogDistribucionPage
 *
 * Dos secciones:
 *   1. Productos con backlog (denegaciones pendientes, sin distribuir).
 *   2. Denegaciones agregadas por producto (cuántas tiendas afectadas).
 */
export function BacklogDistribucionPage(): React.ReactElement {
  const { setTitle } = usePageTitle();
  const [search, setSearch] = useState("");
  const { data: backlog, isLoading: backlogLoading } = useDistributionBacklog();
  const { data: denials } = usePendingDenials();

  useEffect(() => {
    setTitle("Distribución — Backlog");
    return () => setTitle("");
  }, [setTitle]);

  const filteredBacklog = useMemo(
    () =>
      (backlog ?? []).filter((b) =>
        !search.trim() ||
        b.unified_product_id.toLowerCase().includes(search.toLowerCase()),
      ),
    [backlog, search],
  );

  const filteredDenials = useMemo(
    () =>
      (denials ?? []).filter((d) =>
        !search.trim() ||
        d.unified_product_id.toLowerCase().includes(search.toLowerCase()),
      ),
    [denials, search],
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <BacklogIcon color="primary" />
          <Typography variant="h4" component="h1" fontWeight={600}>
            Backlog de distribución
          </Typography>
        </Stack>

        <TextField
          size="small"
          fullWidth
          label="Buscar por unified_product_id"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Paper variant="outlined">
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Productos en backlog ({filteredBacklog.length})
            </Typography>
          </Box>
          {backlogLoading && (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          )}
          {!backlogLoading && filteredBacklog.length === 0 && (
            <Alert severity="success" sx={{ m: 2 }}>
              No hay productos en backlog.
            </Alert>
          )}
          {filteredBacklog.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell align="right">Denegaciones</TableCell>
                    <TableCell align="right">Qty total denegada</TableCell>
                    <TableCell>Tiendas afectadas</TableCell>
                    <TableCell>Denegación más vieja</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBacklog.map((b) => (
                    <TableRow key={b.unified_product_id} hover>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {b.unified_product_id}
                      </TableCell>
                      <TableCell align="right">
                        {b.pending_denials_count}
                      </TableCell>
                      <TableCell align="right">
                        {b.pending_denials_qty.toLocaleString("es-MX")}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                          {b.affected_stores.slice(0, 8).map((s) => (
                            <Chip key={s} size="small" label={s} />
                          ))}
                          {b.affected_stores.length > 8 && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`+${b.affected_stores.length - 8}`}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {new Date(b.oldest_denial_date).toLocaleDateString(
                          "es-MX",
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Paper variant="outlined">
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Denegaciones por producto ({filteredDenials.length})
            </Typography>
          </Box>
          {filteredDenials.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell align="right">Total qty</TableCell>
                    <TableCell align="right">Tiendas</TableCell>
                    <TableCell>Detalle</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDenials.map((d) => (
                    <TableRow key={d.unified_product_id} hover>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {d.unified_product_id}
                      </TableCell>
                      <TableCell align="right">{d.total_qty_denied}</TableCell>
                      <TableCell align="right">{d.stores.length}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {d.stores
                            .slice(0, 3)
                            .map((s) => `${s.store}:${s.qty} (${s.days_open}d)`)
                            .join(", ")}
                          {d.stores.length > 3 && " ..."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}
